import React from 'react';
import { UserX } from 'lucide-react';
import {
  UI_BOAT_AXIS_LABEL,
  uiSeatBadge,
  uiSeatCard,
  type SeatChipState,
} from '../../ui/seatClasses';

export interface SeatData {
  position: number;
  label: string;
  isAssigned: boolean;
  displayName: string;
  isCox?: boolean;
}

interface BoatHullVisualizationProps {
  seats: SeatData[];
  selectedSeat: number | null;
  onSeatClick: (position: number) => void;
  onUnassign?: (position: number) => void;
  disabled?: boolean;
}

function seatState(seat: SeatData, selectedSeat: number | null): SeatChipState {
  if (selectedSeat === seat.position) return 'selected';
  if (seat.isAssigned) return 'assigned';
  return 'default';
}

const BoatHullVisualization: React.FC<BoatHullVisualizationProps> = ({
  seats,
  selectedSeat,
  onSeatClick,
  onUnassign,
  disabled = false,
}) => {
  const rowerSeats = seats.filter((s) => !s.isCox);
  const coxSeat = seats.find((s) => s.isCox) || null;

  const renderSeatCard = (seat: SeatData) => {
    const { position, label, isAssigned, displayName, isCox } = seat;
    const state = seatState(seat, selectedSeat);

    return (
      <div className={uiSeatCard(state)}>
        <button
          type="button"
          onClick={() => onSeatClick(position === selectedSeat ? -1 : position)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left cursor-pointer"
        >
          <div className={uiSeatBadge(state)}>{isCox ? 'C' : position}</div>

          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-content-muted leading-none">{label}</div>
            <div className="text-xs font-semibold truncate mt-0.5">
              {isAssigned ? (
                <span className="text-content-primary">{displayName}</span>
              ) : (
                <span className="text-content-muted italic">Empty</span>
              )}
            </div>
          </div>
        </button>

        {isAssigned && onUnassign && (
          <button
            type="button"
            aria-label={`Unassign ${label}`}
            onClick={() => onUnassign(position)}
            disabled={disabled}
            className="flex-shrink-0 p-0.5 text-rowing-red hover:text-rowing-crimson rounded hover:bg-rowing-red/5 disabled:opacity-50"
          >
            <UserX size={13} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1.5 w-[200px]">
      <div className={UI_BOAT_AXIS_LABEL}>
        <span className="flex-1 h-px bg-border" />
        <span>Bow</span>
        <span className="flex-1 h-px bg-border" />
      </div>

      {rowerSeats.map((seat) => (
        <div key={seat.position}>{renderSeatCard(seat)}</div>
      ))}

      {coxSeat && <div className="mt-1 pt-1 border-t border-border">{renderSeatCard(coxSeat)}</div>}

      <div className={UI_BOAT_AXIS_LABEL}>
        <span className="flex-1 h-px bg-border" />
        <span>Stern</span>
        <span className="flex-1 h-px bg-border" />
      </div>
    </div>
  );
};

export default BoatHullVisualization;
